import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import { supabase } from '../../lib/supabase';
import { getDefaultDashboard } from '../../utils/roleAccess';
import SiteProofLogo from '../../components/SiteProofLogo';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const DemoLogin = () => {
  const navigate = useNavigate();
  const { isDemoAdmin } = useAuth();
  const { t } = useLanguage();
  const [loadingRole, setLoadingRole] = useState(null);
  const [error, setError] = useState(null);

  const DEMO_ROLES = [
    {
      role: 'Admin',
      roleTitle: t('demo.role.admin_title'),
      badge: t('demo.role.admin_badge'),
      description: t('demo.role.admin_desc'),
      demoUser: 'Michael Scott',
      email: import.meta.env?.VITE_DEMO_ADMIN_EMAIL || '',
      password: import.meta.env?.VITE_DEMO_ADMIN_PASSWORD || '',
      icon: 'ShieldCheck',
      color: 'var(--color-accent)',
      bgClass: 'bg-accent/10 border-accent/30 hover:border-accent/60',
      textClass: 'text-accent',
      badgeClass: 'bg-blue-100 text-blue-700'
    },
    {
      role: 'Site Manager',
      roleTitle: t('demo.role.manager_title'),
      badge: t('demo.role.manager_badge'),
      description: t('demo.role.manager_desc'),
      demoUser: 'Dwight Schrute',
      email: import.meta.env?.VITE_DEMO_MANAGER_EMAIL || '',
      password: import.meta.env?.VITE_DEMO_MANAGER_PASSWORD || '',
      icon: 'HardHat',
      color: 'var(--color-primary)',
      bgClass: 'bg-primary/10 border-primary/30 hover:border-primary/60',
      textClass: 'text-primary',
      badgeClass: 'bg-amber-100 text-amber-700'
    },
    {
      role: 'Worker',
      roleTitle: t('demo.role.worker_title'),
      badge: t('demo.role.worker_badge'),
      description: t('demo.role.worker_desc'),
      demoUser: 'Jim Halpert',
      email: import.meta.env?.VITE_DEMO_WORKER_EMAIL || '',
      password: import.meta.env?.VITE_DEMO_WORKER_PASSWORD || '',
      icon: 'Wrench',
      color: 'var(--color-warning)',
      bgClass: 'bg-warning/10 border-warning/30 hover:border-warning/60',
      textClass: 'text-warning',
      badgeClass: 'bg-green-100 text-green-700'
    },
    {
      role: 'Client',
      roleTitle: t('demo.role.client_title'),
      badge: t('demo.role.client_badge'),
      description: t('demo.role.client_desc'),
      demoUser: 'Pam Beesly',
      email: import.meta.env?.VITE_DEMO_CLIENT_EMAIL || '',
      password: import.meta.env?.VITE_DEMO_CLIENT_PASSWORD || '',
      icon: 'Eye',
      color: 'var(--color-secondary)',
      bgClass: 'bg-secondary/10 border-secondary/30 hover:border-secondary/60',
      textClass: 'text-secondary',
      badgeClass: 'bg-purple-100 text-purple-700'
    }
  ];

  // /demo must always show the role selection cards first

  const handleDemoLogin = async (demo) => {
    setLoadingRole(demo?.role);
    setError(null);

    const { data, error: signInError } = await authService?.signIn(demo?.email, demo?.password);

    if (signInError) {
      setError(`Could not sign in as ${demo?.role}. Please use "Create / Reset Demo Users" on the login page if this is your first time, then try again.`);
      setLoadingRole(null);
      return;
    }

    const { data: { user: signedInUser } } = await supabase?.auth?.getUser();
    if (signedInUser) {
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('role, company_id')
        ?.eq('id', signedInUser?.id)
        ?.single();

      const role = profile?.role;
      const demoAdminEmail = import.meta.env?.VITE_DEMO_ADMIN_EMAIL;

      // Admin demo: always skip setup_complete check — go directly to admin dashboard
      if (role === 'admin' || (demoAdminEmail && signedInUser?.email === demoAdminEmail)) {
        navigate('/admin-dashboard', { replace: true });
      } else {
        navigate(getDefaultDashboard(role), { replace: true });
      }
    } else {
      navigate('/admin-dashboard', { replace: true });
    }

    setLoadingRole(null);
  };

  // Sign out any existing session, then go to the real login page
  const handleGoToLogin = async (e) => {
    e?.preventDefault();
    await supabase?.auth?.signOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12">
        <div className="w-full max-w-md">
          {/* Back to landing */}
          <div className="mb-6 flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="ArrowLeft" size={16} />
              {t('demo.back_to_home')}
            </Link>
            <button
              type="button"
              onClick={handleGoToLogin}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors bg-transparent border-none p-0 cursor-pointer"
            >
              {t('demo.sign_in_instead')}
            </button>
          </div>

          <div className="bg-card rounded-lg shadow-elevation-3 p-6 md:p-8 border border-border">
            {/* Logo */}
            <div className="flex flex-col items-center mb-6">
              <div className="mb-4">
                <SiteProofLogo size="lg" />
              </div>
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent/10 rounded-full mb-2">
                  <Icon name="Sparkles" size={14} color="var(--color-accent)" />
                  <span className="text-xs font-medium text-accent">{t('demo.live_demo_badge')}</span>
                </div>
                <h1 className="text-xl font-bold text-foreground font-heading">{t('demo.choose_role')}</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('demo.subtitle')}
                </p>
              </div>
            </div>

            {/* Error banner */}
            {error && (
              <div className="mb-4 flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <Icon name="AlertCircle" size={15} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                <p className="text-xs text-destructive leading-snug">{error}</p>
              </div>
            )}

            {/* Demo role cards */}
            <div className="grid grid-cols-1 gap-2.5">
              {DEMO_ROLES?.map((demo) => {
                const isLoading = loadingRole === demo?.role;
                const isDisabled = loadingRole !== null;
                return (
                  <button
                    key={demo?.role}
                    type="button"
                    onClick={() => handleDemoLogin(demo)}
                    disabled={isDisabled}
                    className={`
                      w-full text-left px-4 py-3.5 rounded-lg border-2 transition-all duration-150
                      ${demo?.bgClass}
                      ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:scale-[1.02] hover:shadow-md active:scale-[0.99]'}
                    `}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-0.5">
                        {isLoading ? (
                          <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" style={{ color: demo?.color }} />
                        ) : (
                          <Icon name={demo?.icon} size={20} color={demo?.color} />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-base font-bold leading-tight ${demo?.textClass}`}>
                          {isLoading ? `${t('demo.signing_in_as')} ${demo?.demoUser}…` : demo?.roleTitle}
                        </p>
                        <span className={`inline-flex items-center mt-1 mb-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide uppercase ${demo?.badgeClass}`}>
                          {demo?.badge}
                        </span>
                        <p className="text-xs text-foreground/75 leading-snug">
                          {demo?.description}
                        </p>
                        <p className="text-[11px] text-muted-foreground mt-1.5">
                          {t('demo.demo_user_label')} <span className="font-medium">{demo?.demoUser}</span>
                        </p>
                      </div>

                      {/* Arrow */}
                      {!isLoading && (
                        <Icon name="ArrowRight" size={20} color={demo?.color} className="flex-shrink-0 mt-1 opacity-60" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer note */}
            <p className="text-[10px] text-muted-foreground text-center mt-4 leading-relaxed">
              {t('demo.footer_note')}{' '}
              <button
                type="button"
                onClick={handleGoToLogin}
                className="font-medium underline bg-transparent border-none p-0 cursor-pointer text-[10px] text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('demo.footer_link')}
              </button>{' '}
              {t('demo.footer_note2')}
            </p>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground">
              {t('demo.want_own_account')}{' '}
              <Link to="/company-registration" className="font-medium text-accent hover:text-accent/80 transition-colors">
                {t('demo.register_company')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoLogin;
